"use client";
import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Reveal } from "../reveal";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Form, Formik } from "formik";
import { GuidesSearchSection, GuidesSection } from "./guides-section";
import { SearchInput } from "../inputs/search";

type Feature = "buy-for-me" | "wallet" | "store" | "ship" | "general";

const FEATURES: Record<Feature, string> = {
  "buy-for-me": "Buy for Me",
  wallet: "Wallet",
  store: "Store",
  ship: "Ship",
  general: "General",
};

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function GuidesSections() {
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const features = Object.keys(FEATURES) as Feature[];

  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");

    if (!tabFromUrl) return;

    const tabIndex = features.findIndex((tab) => tab === tabFromUrl);

    if (tabIndex !== -1) {
      setCurrentTabIndex(tabIndex);
    }
  }, [searchParams, features]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const getTabClickHandler = (feature: string, index: number) => {
    return () => {
      setCurrentTabIndex(index);
      router.replace(`?tab=${feature}`, { scroll: false });
    };
  };

  return (
    <section className="max-w-lg md:max-w-4xl lg:max-w-6xl xl:max-w-screen-2xl mx-auto py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-white">
      <Formik
        initialValues={{ search: "" }}
        onSubmit={() => {
          return;
        }}
      >
        <Form className="flex flex-col items-center justify-center gap-8 py-10">
          <div className="flex items-center justify-center gap-3 max-w-2xl w-full">
            <SearchInput
              name="search"
              placeholder="Ask a question"
              value={searchTerm}
              onChange={handleSearchChange}
              className="flex-1"
            />
          </div>
        </Form>
      </Formik>

      <Reveal initialY={"10%"}>
        {debouncedSearchTerm ? (
          <AnimatePresence mode="wait">
            <motion.div
              key="search-results"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -30, opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <GuidesSearchSection searchQuery={debouncedSearchTerm} />
            </motion.div>
          </AnimatePresence>
        ) : (
          <Tabs
            defaultValue={features[0]}
            value={features[currentTabIndex]}
            className="flex flex-col md:flex-row xl:flex-row gap-4 md:gap-8 lg:gap-12 xl:gap-24"
          >
            <div className="md:flex md:justify-start w-full md:w-auto">
              <ScrollArea className="w-full md:w-fit whitespace-nowrap">
                <TabsList className="flex md:flex-col pt-8 xl:flex-col flex-row h-full bg-white items-start gap-3 md:gap-4 w-full md:w-auto justify-center">
                  {features.map((feature, index) => (
                    <TabsTrigger value={feature} key={feature} asChild>
                      <button
                        onClick={getTabClickHandler(feature, index)}
                        className="relative py-2 px-3 md:px-4 text-xs md:text-sm lg:text-base gap-2 cursor-pointer data-[state=active]:text-white group data-[state=active]:bg-transparent data-[state=active]:shadow-none transition duration-500 whitespace-nowrap md:whitespace-normal hover:bg-primary-400/80 hover:text-white"
                      >
                        {currentTabIndex === index && (
                          <motion.div
                            layoutId="guide-tab"
                            className="absolute h-full w-full group-data-[state=active]:bg-primary-400 group-data-[state=active]:shadow-sm group-data-[state=active]:rounded-sm group-data-[state=active]:hover:bg-primary-400/80"
                          />
                        )}
                        <span className="relative">{FEATURES[feature]}</span>
                      </button>
                    </TabsTrigger>
                  ))}
                </TabsList>

                <ScrollBar orientation="horizontal" className="h-0.5" />
              </ScrollArea>
            </div>

            {features.map((feature) => (
              <TabsContent value={feature} key={feature} className="flex-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={feature}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex flex-col md:flex-col lg:flex-row gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-24"
                  >
                    <GuidesSection feature={feature} />
                  </motion.div>
                </AnimatePresence>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </Reveal>
    </section>
  );
}
